import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2, CheckCircle2, AlertTriangle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [phone, setPhone] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) { setErr('Missing invite token'); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/invites/accept?token=${encodeURIComponent(token)}`);
        setInvite(res.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Invite not found or expired');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accept = async () => {
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== password2) { toast.error('Passwords do not match'); return; }
    if (!phone.trim()) { toast.error('Phone number is required'); return; }
    setAccepting(true);
    try {
      const res = await axios.post(`${BACKEND}/api/invites/accept`, {
        token, password, phone: phone.trim(),
      });
      // Extract JWT (backend returns tuple [token, session_id] or string)
      const tok = Array.isArray(res.data.token) ? res.data.token[0] : res.data.token;
      if (tok) {
        localStorage.setItem('auth_token', tok);
        localStorage.setItem('user_role', res.data.role);
      }
      toast.success('Welcome aboard!');
      // Route to the correct home
      navigate(res.data.role === 'agency' ? '/client/home' : '/client/home');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invite');
    } finally { setAccepting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="accept-invite-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-cyan-500" />
            {err ? 'Invite issue' : 'Join the team'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {err && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>{err}</div>
            </div>
          )}
          {invite && (
            <>
              <div className="p-3 rounded-md bg-cyan-50 border border-cyan-200 text-sm text-cyan-900 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-cyan-500" />
                <div>
                  <p className="font-medium">{invite.inviter_name} invited you to join their company.</p>
                  <p className="text-xs mt-0.5">Email: {invite.email} · Role: {invite.role}</p>
                </div>
              </div>

              <label className="text-xs text-gray-600 space-y-1 block">
                <span className="block font-medium">Phone</span>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 555 5555" data-testid="accept-phone-input" />
              </label>
              <label className="text-xs text-gray-600 space-y-1 block">
                <span className="block font-medium">Password</span>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="accept-password-input" />
              </label>
              <label className="text-xs text-gray-600 space-y-1 block">
                <span className="block font-medium">Confirm password</span>
                <Input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} data-testid="accept-password2-input" />
              </label>

              <Button
                onClick={accept}
                disabled={accepting}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="accept-invite-submit"
              >
                {accepting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Accept & create account
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvite;
